/**
 * Mobile Header Component - MPAD Implementation
 * Responsive navigation header for mobile devices
 * 
 * Features:
 * - Hamburger menu for sidebar toggle
 * - Project selector
 * - User profile menu
 * - Touch-friendly interactions (≥44px targets)
 */

import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  useTheme, 
  useMediaQuery,
  Box 
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';

interface MobileHeaderProps {
  onMenuToggle: () => void;
  title?: string;
  subtitle?: string;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  onMenuToggle,
  title = "VRP System",
  subtitle
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Only render on mobile
  if (!isMobile) {
    return null;
  }

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        top: 48, // Below main ribbon
        backgroundColor: 'var(--surface-primary)',
        borderBottom: '1px solid var(--border-subtle)',
        color: 'var(--text-primary)',
        height: 56, // Standard mobile header height
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Toolbar
        sx={{
          minHeight: '56px !important',
          px: 2,
        }}
      >
        {/* Hamburger Menu Button */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="Open navigation menu"
          onClick={onMenuToggle}
          sx={{
            mr: 2,
            width: 44, // Touch target size
            height: 44,
            color: 'var(--text-primary)',
            '&:hover': {
              backgroundColor: 'var(--surface-tertiary)',
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Title and Subtitle */}
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="h6"
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: '1rem',
              color: 'var(--text-primary)',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'var(--text-secondary)',
                fontSize: '0.75rem',
                lineHeight: 1,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Optional: Add mobile-specific actions here */}
      </Toolbar>
    </AppBar>
  );
};

export default MobileHeader;